/* Common imports */
import { ObjectId } from "bson";
import React from "react";

/* Models import*/
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../models";

/* Redux imports */
import StateInterface from "../../../redux-magic/state-interface";
import { PropertySnackbarInterface } from "../../../redux-magic/sub-interfaces/property-snackbar";
import { addPropertyImagesThunk, setPropertySnackbarThunk } from "../../../redux-magic/thunks";

/* Component Imports */
import BeegruButton from "../../common-components/buttons/BeegruButton";

/* mui imports */
import {
	Alert,
	Chip,
	CircularProgress,
	Dialog,
	FormControl,
	IconButton,
	MenuItem,
	RadioGroup,
	Select,
	SelectChangeEvent,
	Slide,
	Snackbar,
	SvgIcon,
	Typography,
	useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { styled, useMediaQuery } from "@mui/system";

/* router import */
import { useRouter } from "next/router";

/* Library imports */
import uploadFiles from "../../../lib/uploadFiles";
import validateFiles from "../../../lib/validateFiles";

/* Icons Imports */
import { Close, DeleteOutline } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RequestImagesIcon from "../../../public/images/icons/singleproperty/request_image.svg";

/* Interfaces */
interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

interface FileAndMore {
	file: File;
	mime_type: string;
}

/* Styled Components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
		},
		// minHeight: "700px",
		maxHeight: "100%",
		borderRadius: "1rem",
		background: theme.palette.background.paper,
	},
}));

const CustomDialogTitle = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 1rem 0.5rem 1rem",
	background: theme.palette.background.default,
}));

const CustomDialogContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "100%",
	height: "100%",
	gap: "1rem",
	background: theme.palette.background.default,
	overflow: "scroll",
	scrollbarWidth: "none",
	padding: "1rem",
}));

const CustomDialogActions = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
	padding: "1rem",
	background: theme.palette.background.default,
}));

const InputContent = styled("input")(({ theme }) => ({
	display: "none",
}));

const UploadContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	border: "1px dashed var(--Other-New-Outline-Border, rgba(0, 0, 0, 0.23))",
	borderRadius: "1rem",
	cursor: "pointer",
	borderColor: "#0000003B",
	// height: "10rem",
	padding: "2rem",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "47%",
	height: "10.625rem",
	borderRadius: "16px",
	borderColor: theme.palette.primary.main,
}));

const ImageContainer = styled("div")(({ theme }) => ({
	position: "relative",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	cursor: "pointer",
}));

const PreviewImage = styled("img")(({ theme }) => ({
	width: "100%",
	height: "10.238rem",
	borderRadius: "16px ",
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	// [theme.breakpoints.down("md")]: {
	// 	bottom: "5rem",
	// 	borderRadius: "0.5rem",
	// },
}));

const FormControlContainer = styled(FormControl)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	padding: "var(--Spacing-spacing-sm, 0.5rem)",
	gap: "var(--Spacing-spacing-sm, 0.5rem)",
	alignSelf: "stretch",
	width: "100%",
	height: "3rem",
	position: "absolute",
	bottom: "0.3rem",
}));

const ProgressContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	position: "absolute",
	width: "100%",
	height: "100%",
	zIndex: 0,
}));

const ImagePreviewContainer = styled(RadioGroup)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	gap: "1rem",
	flexWrap: "wrap",
}));

const SecondaryUploadContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "14.85rem",
	height: "10.238rem",
	border: "2px dashed #EE4B2B",
	borderRadius: "16px",
	cursor: "pointer",
	borderColor: theme.palette.primary.main,
}));

/* Dialog Transition */

const DialogTransition = React.forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<any, any>;
	},
	ref: React.Ref<unknown>,
) {
	return (
		<Slide
			direction="up"
			ref={ref}
			{...props}
		/>
	);
});

const UploadImageModal = ({
	open,
	handleClose,
	propertyData,
	index,
	session,
	dispatch,
	isEditor,
	profile_context,
	property_snackbar,
}: {
	open: boolean;
	handleClose: any;
	propertyData: LocalPropertyInterface;
	index: number;
	session: any;
	dispatch: Function;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	property_snackbar: PropertySnackbarInterface;
}) => {
	const theme = useTheme();

	const input = React.useRef<HTMLInputElement>(null);

	const [selectedFiles, setSelectedFiles] = React.useState<Array<FileAndMore>>([]);

	const [percentage, setPercentage] = React.useState<Array<number>>([0]);

	const [stage, setStage] = React.useState<Array<string>>(["NOT_STARTED"]);

	const allFilesFetched = stage.every((status) => status === "FETCHED_FILE_INFORMATION");

	const [fileId, setFileId] = React.useState<Array<ObjectId | null>>([null]);

	const [loading, setLoading] = React.useState<boolean>(false);

	const [openSnackbar, setOpenSnackbar] = React.useState(false);

	const [selectedFilePreview, setSelectedFilePreview] = React.useState<Array<string>>([]);

	const [fileErrorSnackbarText, setFileErrorSnackbarText] = React.useState<string>("File size or type not supported");

	const [category, setCategory] = React.useState<Array<string>>([]);

	const [load, setLoad] = React.useState<boolean>(false);

	const [isFileDialogOpen, setFileDialogOpen] = React.useState(false);

	const [priority, setPriority] = React.useState<number>(0);

	const [prioritySelected, setPrioritySelected] = React.useState<boolean>(false);

	const hasExistingImages = (propertyData?.images?.length || 0) > 0;

	const smDevice: boolean = useMediaQuery(theme.breakpoints.down("sm"));

	const router = React.useRef(useRouter()).current;

	/* Let's set the Local language */
	const localLang = {
		sizeOrTypeNotSupported:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಫೈಲ್ ಗಾತ್ರ ಅಥವಾ ಪ್ರಕಾರ ಬೆಂಬಲಿಸಲ್ಪಟ್ಟಿಲ್ಲ "
					: "File size or type not supported",
		isAn: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಒಂದು" : " is an",
		are: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಇವು" : " are",
		unsupported: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಅಸಮರ್ಥವಾಗಿದೆ" : " unsupported",
		file: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಫೈಲ್" : " file",
		files: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಫೈಲ್ಗಳು" : " files",
		and: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಮತ್ತು" : " and",
		has: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಹೊಂದಿದೆ" : " has",
		have: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಹೊಂದಿದುವು" : " have",
		beenDeselected:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? " ಸ್ವಯಂಚಾಲಿತವಾಗಿ ರದ್ದುಗೊಂಡಿದೆ."
					: " been deselected automatically.",
		jpegOrPng: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "JPEG ಅಥವಾ PNG" : "JPEG or PNG",
		imgMaxSize:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಚಿತ್ರ ಗಾತ್ರ ಗರಿಷ್ಠ 10MB" : "Max upload 10MB",
		category: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ವರ್ಗ" : "Category",
		exterior: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಬಾಹ್ಯ" : "Exterior",
		interior: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಆಂತರಿಕ" : "Interior",
		masterPlan: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಮಾಸ್ಟರ್ ಪ್ಲಾನ್" : "Master plan",
		amenities: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸೌಲಭ್ಯಗಳು" : "Amenities",
		routMap: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ರೂಟ್ ಮ್ಯಾಪ್" : "Route map",
		addMore: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಹೆಚ್ಚು ಸೇರಿಸಿ" : "Add More",
		addPhotosFromYourGallary:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಗ್ಯಾಲರಿ ನಿಂದ ಫೋಟೋಗಳನ್ನು ಸೇರಿಸಿ"
					: "Add photos from your gallery",
		maxLimit:
			router.locale === "ar-AE"
				? "ملف JPG أو PNG، بحجم لا يزيد عن 10 ميجابايت."
				: router.locale === "kn-IN"
					? "JPG ಅಥವಾ PNG ಫೈಲ್, ಗಾತ್ರವು 10MB ಗಿಂತ ಕಡಿಮೆ ಇರಬೇಕು."
					: "JPG or PNG, file size no more than 10MB",
		moreThePixelMoreTheReach:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಹೆಚ್ಚು ಪಿಕ್ಸೆಲ್ ಹೆಚ್ಚು ತಲುಪುತ್ತದೆ"
					: "More the pixels more the reach",
		startUploading:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಅಪ್ಲೋಡ್ ಪ್ರಾರಂಭಿಸಿ" : "Start Uploading",
		coverImageText:
			router.locale === "ar-AE"
				? "اضغط على الصورة المحددة لجعلها صورة الغلاف"
				: router.locale === "kn-IN"
					? "ಆಯ್ದ ಚಿತ್ರದ ಮೇಲೆ ಟ್ಯಾಪ್ ಮಾಡಿ, ಅದನ್ನು ಕವರ್ ಚಿತ್ರವನ್ನಾಗಿ ಮಾಡಿರಿ"
					: "Tap on selected image to make it cover image",
		uploadImages:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಚಿತ್ರಗಳನ್ನು ಅಪ್ಲೋಡ್ ಮಾಡಿ" : "Upload images",
		save: router.locale === "ar-AE" ? "حفظ" : router.locale === "kn-IN" ? "ಉಳಿಸಿ" : "Save",
	};

	/* Now, Let's create an upload progress interface */
	interface UploadProgress {
		fileId: ObjectId | null;
		percentage: number;
		stage: string;
	}

	const handleUpload = async (validFiles: Array<FileAndMore>, filePreview: Array<string>) => {
		setLoad(true);
		try {
			await uploadFiles(
				session ? (session.authorized ? (session.session_id ? session.session_id : "") : "") : "",
				validFiles.map((file: FileAndMore) => ({
					file: file.file,
					mime_type: file.mime_type,
					original_file_id: new ObjectId(),
					original_file_server_path: "",
					original: true,
					is_business_profile: propertyData
						? propertyData.created_by
							? propertyData.created_by.is_business_profile
							: false
						: false,
					business_profile_id: propertyData
						? propertyData.created_by
							? propertyData.created_by.business_profile_id
								? new ObjectId((propertyData.created_by.business_profile_id as BusinessProfileInterface)._id)
								: new ObjectId()
							: new ObjectId()
						: new ObjectId(),
					entity: "property_" + (propertyData ? (propertyData.is_ofb ? "ofb" : "regular") : "regular"),
					entity_id: propertyData ? new ObjectId(propertyData._id) : new ObjectId(),
					use_case: "property_image",
					uiPath: "/",
					isPublic: false,
					text: propertyData.created_by.is_business_profile
						? (propertyData.created_by.business_profile_id as BusinessProfileInterface).title
						: (propertyData.created_by.user_id as UserInterface).first_name +
							" " +
							(propertyData.created_by.user_id as UserInterface).last_name,
				})),
				(uploadProgress: Array<UploadProgress>) => {
					const data = uploadProgress.map((progress) => progress.fileId);

					const uploadedProg = uploadProgress.map((progress) => progress);

					const stageInfo = uploadProgress.map((progress) => progress.stage);

					const percentageInfo = uploadProgress.map((progress) => progress.percentage);

					fileId[0] != null ? setFileId([...fileId, ...data]) : setFileId(data);

					percentage[0] != 0 ? setPercentage([...percentage, ...percentageInfo]) : setPercentage(percentageInfo);

					const allFetched = uploadedProg.every((progress) => progress.stage === "FETCHED_FILE_INFORMATION");

					if (allFetched) {
						stage[0] != "NOT_STARTED" ? setStage([...stage, ...stageInfo]) : setStage(stageInfo);
					}
				},
			);
		} catch (error) {
			console.error("[PROPERTY_IMAGE_UPLOAD]", error);
			setFileErrorSnackbarText("Upload failed. Please try again.");
			setOpenSnackbar(true);
			setSelectedFiles([]);
			setSelectedFilePreview([]);
			setStage(["NOT_STARTED"]);
			setFileId([null]);
			setPercentage([0]);
		} finally {
			setLoad(false);
		}
	};

	/* Submit function to save the images to the property collection */

	const handleSubmit = async () => {
		setLoading(true);
		const property_id: ObjectId | string = propertyData._id;
		await dispatch(
			addPropertyImagesThunk({
				property_id,
				file_id: fileId,
				priorityIndex: priority,
				isPrioritySelected: prioritySelected,
				category: category,
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				mime_type: selectedFiles.map((file: FileAndMore) => file.mime_type),
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: propertyData.created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				profile_context: profile_context,
			}),
		);
		setSelectedFiles([]);
		setSelectedFilePreview([]);
		setLoading(false);
		setStage(["NOT_STARTED"]);
		setFileId([null]);
		setPercentage([0]);
		handleUploadClose();
	};

	/* Now, Let's create a function to remove trhe uploaded image */
	const handleRemoveImage = async (index: number) => {
		const newSelectedFilePreview = [...selectedFilePreview];
		const newSelectedFiles = [...selectedFiles];
		const newFileId = [...fileId];
		const newStage = [...stage];

		newSelectedFilePreview.splice(index, 1);
		newSelectedFiles.splice(index, 1);
		newFileId.splice(index, 1);
		newStage.splice(index, 1);

		// Update the state with the new arrays
		setSelectedFilePreview(newSelectedFilePreview);
		setSelectedFiles(newSelectedFiles);
		setFileId(newFileId);
		setStage(newStage);
	};

	/* Next, Let's create a functrion to close the modal and let's set the local state to its default value */
	const handleUploadClose = () => {
		setSelectedFiles([]);
		setSelectedFilePreview([]);
		setLoading(false);
		setStage(["NOT_STARTED"]);
		setCategory(["all_images"]);
		setFileId([null]);
		setPercentage([0]);
		handleClose();
	};

	/* Next, Let's create a function to select the category for the selected image */
	const handleSelect = (event: SelectChangeEvent, index: number) => {
		// Let's check if the value is not empty or undefined
		// and then set the category to the selected value
		if (event.target.value !== "" && event.target.value !== undefined) {
			setCategory((prev) => {
				const updated = [...prev];
				updated[index] = event.target.value.replace(" ", "_").toLowerCase();
				return updated;
			});
		} else {
			// If the value is empty or undefined, set the category to "exterior"
			setCategory((prev) => {
				const updated = [...prev];
				updated[index] = "exterior";
				return updated;
			});
		}
	};

	// Let's create a function to format the category name
	// and make it more readable
	const formatCategory = (category: string) => {
		return category
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	// Let's create a function to handle the click event
	React.useEffect(() => {
		if (open) {
			setFileDialogOpen(true);
		}
	}, [open]);

	React.useEffect(() => {
		if (isFileDialogOpen && input.current) {
			input.current.value = "";
			input.current.click();
			// Set isFileDialogOpen to false after opening the dialog
			setFileDialogOpen(false);
		}
	}, [isFileDialogOpen]);

	return (
		<React.Fragment>
			<Snack
				open={openSnackbar}
				autoHideDuration={3000}
				onClose={() => setOpenSnackbar(false)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => setOpenSnackbar(false)}
					severity="error"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{fileErrorSnackbarText}
				</Alert>
			</Snack>
			<Snack
				open={property_snackbar ? (property_snackbar.property_images_upload as boolean) : false}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					Uploaded successfully
				</Alert>
			</Snack>

			{/* Upload Image Modal */}

			<CustomDialog
				open={open}
				maxWidth="xs"
				fullWidth={true}
				fullScreen={smDevice}
				TransitionComponent={DialogTransition}
			>
				<CustomDialogTitle variant="h6">
					{localLang.uploadImages}
					<IconButton
						aria-label="close"
						onClick={handleUploadClose}
					>
						{loading ? null : <Close />}
					</IconButton>
				</CustomDialogTitle>

				<CustomDialogContent>
					<InputContent
						ref={input}
						type="file"
						accept="image/jpeg, image/png"
						multiple={true}
						onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
							const files = event.target.files;
							const allowedTypes = ["image/jpeg", "image/png"];
							const allowedExtensions = ["jpg", "jpeg", "png"];
							const uploadType: string = "property_or_services";
							if (files && files.length > 0) {
								const {
									validFiles,
									invalidFiles,
								}: {
									validFiles: Array<FileAndMore>;
									invalidFiles: Array<File>;
								} = await validateFiles(Array.from(files), allowedTypes, allowedExtensions, uploadType);

								if (validFiles.length > 0) {
									const previews = validFiles.map((f) => URL.createObjectURL(f.file));
									setSelectedFilePreview((prev) => [...prev, ...previews]);
									setSelectedFiles((prev) => [...prev, ...validFiles]);
									// setCategory(validFiles.map(() => ""));
									handleUpload(validFiles, [...selectedFilePreview, ...previews]);
								}
								if (invalidFiles.length > 0) {
									const fileNames: Array<string> = invalidFiles.map((file: File) => file.name);
									setFileErrorSnackbarText(
										(fileNames.join(", ").length > 15
											? fileNames.join(", ").slice(0, 15) + "..."
											: fileNames.join(", ")) +
											(invalidFiles.length === 1 ? " is an" : " are") +
											" unsupported " +
											(invalidFiles.length === 1 ? "file" : "files") +
											" and " +
											(invalidFiles.length === 1 ? "has" : "have") +
											" been deselected automatically.",
									);
									setOpenSnackbar(true);
								} else {
									setOpenSnackbar(false);
								}
							} else {
								setOpenSnackbar(true);
							}
						}}
					/>
					{selectedFiles.length === 0 && (
						<div style={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
							<UploadContent onClick={() => input.current?.click()}>
								<SvgIcon
									component={RequestImagesIcon}
									viewBox="0 0 41 33"
									width={24}
									height={24}
									sx={{ width: "2.563rem", height: "1.976rem", fill: "none" }}
								/>
								<Typography sx={{ color: theme.palette.text.primary, fontSize: "0.875rem" }}>
									{localLang.addPhotosFromYourGallary}
								</Typography>
								<Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
									{localLang.maxLimit}
								</Typography>
							</UploadContent>
						</div>
					)}

					<ImagePreviewContainer>
						{selectedFilePreview.length > 0 && allFilesFetched && (
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "space-between",
									alignItems: "center",
									width: "100%",
								}}
							>
								<Typography
									sx={{
										color: theme.palette.text.primary,
										fontSize: "1rem",
										fontWeight: 700,
									}}
								>
									{router.locale === "ar-AE"
										? `تم تحميل ${selectedFilePreview.length} ${selectedFilePreview.length > 1 ? "صور" : "صورة"}`
										: router.locale === "kn-IN"
											? `${selectedFilePreview.length} ${selectedFilePreview.length > 1 ? "ಚಿತ್ರಗಳು" : "ಚಿತ್ರ"} ಅಪ್‌ಲೋಡ್ ಮಾಡಲಾಗಿದೆ`
											: `Uploaded ${selectedFilePreview.length} ${selectedFilePreview.length > 1 ? "images" : "image"}`}
								</Typography>
								<BeegruButton
									variant="contained"
									sx={{
										color: theme.palette.primary.main,
										fontSize: "0.875rem",
										fontWeight: 500,
										textTransform: "none",
									}}
									onClick={() => {
										input.current?.click();
									}}
								>
									<AddOutlinedIcon /> {localLang.addMore}
								</BeegruButton>
							</div>
						)}

						{selectedFilePreview.length > 0 && allFilesFetched && (!hasExistingImages || prioritySelected) && (
							<div style={{ display: "flex", position: "relative", width: "100%" }}>
								<Chip
									label="Cover photo"
									sx={{
										position: "absolute",
										top: "0.5rem",
										left: "0.5rem",
										backgroundColor: "#FFF",
										color: "#7E7D7D",
										fontSize: "0.75rem",
										fontWeight: 400,
										borderRadius: "0.75rem",
										boxShadow: "0px 4px 18px rgba(0,0,0,0.05)",
										height: "20px",
										zIndex: 1,
									}}
								/>
								<PreviewImage
									src={selectedFilePreview[priority]}
									alt="Property Images"
									referrerPolicy="no-referrer"
									style={{ width: "100%", height: "10.5rem", borderRadius: "16px" }}
								/>
							</div>
						)}

						{/* <RadioGroup> */}
						{selectedFilePreview.map((file: string, index: number) => (
							<ContentContainer key={index}>
								{file !== "" ? (
									<ImageContainer
										onClick={() => {
											if (prioritySelected && priority === index) {
												setPrioritySelected(false);
												setPriority(0);
											} else {
												setPriority(index);
												setPrioritySelected(true);
											}
										}}
									>
										<PreviewImage
											alt="Property Images"
											src={file}
											referrerPolicy="no-referrer"
										/>
										<ProgressContainer>
											{(stage[index] === undefined ||
												stage[index] === "NOT_STARTED" ||
												(stage[index] && stage[index] !== "FETCHED_FILE_INFORMATION")) && (
												<CircularProgress sx={{ color: "#FFF" }} />
											)}
										</ProgressContainer>
										{stage[index] === "FETCHED_FILE_INFORMATION" && (
											<IconButton
												size="small"
												onClick={() => handleRemoveImage(index)}
												sx={{
													position: "absolute",
													top: "0.3rem",
													right: "0.5rem",
													background: "#FFFFFF90",
													padding: "0.5rem",
													height: "2rem",
													width: "2rem",
													zIndex: 1,
													margin: "0.5rem 0rem 0rem 0rem",
												}}
											>
												<DeleteOutline sx={{ color: "#E31B0C", fontSize: "1.25rem" }} />
											</IconButton>
										)}

										{/* Select category */}

										<FormControlContainer size="small">
											<Select
												value={category[index] ? formatCategory(category[index]) : "Exterior"}
												displayEmpty
												renderValue={(selected) => {
													if (selected === "all_images") {
														return <em style={{ fontSize: "0.875rem" }}>{localLang.category}</em>;
													}

													return selected;
												}}
												inputProps={{
													placeholder: "Category",
												}}
												onChange={(event) => handleSelect(event, index)}
												sx={{
													width: "9rem",
													backgroundColor: "#FFFFFF",
												}}
												MenuProps={{
													disableScrollLock: true,
													MenuListProps: {
														sx: {
															backgroundColor: theme.palette.background.paper,
														},
													},
												}}
											>
												<MenuItem value={"Exterior"}>{localLang.exterior}</MenuItem>
												<MenuItem value={"Interior"}>{localLang.interior}</MenuItem>
												<MenuItem value={"Master Plan"}>{localLang.masterPlan}</MenuItem>
												<MenuItem value={"Amenities"}>{localLang.amenities}</MenuItem>
												<MenuItem value={"Route Map"}>{localLang.routMap}</MenuItem>
											</Select>
										</FormControlContainer>
									</ImageContainer>
								) : null}
							</ContentContainer>
						))}
					</ImagePreviewContainer>

					{selectedFilePreview.length > 0 && allFilesFetched && (
						<div
							style={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "flex-start",
								alignItems: "center",
								width: "100%",
								gap: "0.5rem",
								padding: "0.5rem 1rem",
								backgroundColor: "#FFC800",
								borderRadius: "8px",
								boxSizing: "border-box",
							}}
						>
							<InfoOutlinedIcon sx={{ color: "#666" }} />
							<Typography
								variant="caption"
								sx={{ color: "#666", fontSize: "0.75rem", fontWeight: 400 }}
							>
								{localLang.coverImageText}
							</Typography>
						</div>
					)}
				</CustomDialogContent>

				<CustomDialogActions>
					{stage.every((stage) => stage === "FETCHED_FILE_INFORMATION") ? (
						loading ? (
							<CircularProgress />
						) : (
							<BeegruButton
								disabled={selectedFiles.length === 0 ? true : false}
								variant="contained"
								flavor="primary"
								size="large"
								onClick={(event: React.MouseEvent<HTMLElement>) => {
									handleSubmit();
								}}
								name="upload"
								type="button"
							>
								{localLang.save}
							</BeegruButton>
						)
					) : null}
				</CustomDialogActions>
			</CustomDialog>
		</React.Fragment>
	);
};

export default UploadImageModal;
